import { useCallback } from "react";
import useActionDialog from "../../components/ActionDialog";
import {
    FormFieldErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { AddOutlined, CloseOutlined, DeleteOutlined } from "@mui/icons-material";
import { useUserInfo } from "../../utils/security";
import { isEmpty, set } from "lodash";
import NumberField from "../../components/NumberField";
import CurrencyField from "../../components/CurrencyField";
import { formatNum, parseNum } from "../../utils/numbers";
import { calculateTaxItem } from "./calculateTax";

const TransactionItemDialog = ({ open, hide, onChange, data, disabled, idNumberLabel }) => {
    const { userInfo } = useUserInfo();
    const showDialog = useActionDialog();
    const onSubmit = useCallback(
        (values) => {
            onChange?.(values);
            hide();
        },
        [hide, onChange]
    );
    const validate = useCallback(
        (values, errors) => {
            validateMandatoryField(values.name, "name", errors);
            validateNumberField(values.units, "units", errors);
            validateNumberField(values.unitPrice, "unitPrice", errors);
            validateMandatoryField(values.total, "total", errors);
            validateNumberField(values.total, "total", errors);

            if (userInfo?.transactionsTaxSupport) {
                (values.taxItems || []).forEach((field, index) => {
                    validateMandatoryField(field.taxTypeId, `fields.${index}.taxTypeId`, errors);
                    validateMandatoryField(field.base, `fields.${index}.base`, errors);
                    validateMandatoryField(field.total, `fields.${index}.total`, errors);
                });
            }
        },
        [userInfo]
    );

    const mapDataToFields = useCallback(
        ({ units, unitPrice, total, ...values }) => ({
            units: formatNum(units, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            unitPrice: formatNum(
                unitPrice,
                2,
                userInfo.decimalDelimiter,
                userInfo.thousandDelimiter
            ),
            total: formatNum(total, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const mapFieldsToData = useCallback(
        ({ units, unitPrice, total, ...values }) => ({
            units: parseNum(units, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            unitPrice: parseNum(unitPrice, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            total: parseNum(total, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const { values, handleChange, handleSubmit, setValues, errors } = useForm({
        data,
        onSubmit,
        validate,
        mapDataToFields,
        mapFieldsToData,
    });

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={hide}>
            <DialogTitle>Položka</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={hide}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <Box mb={2}>
                    <TextField
                        label="Název"
                        name="name"
                        value={values.name || ""}
                        onChange={handleChange}
                        required
                        margin="normal"
                        fullWidth
                        error={hasErrors(errors, "name")}
                        helperText={<FormFieldErrors errors={errors} field="name" />}
                    />
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <NumberField
                                label={isEmpty(values.uom) ? "Počet" : `Počet ${values.uom}`}
                                name="units"
                                value={values.units || ""}
                                onChange={(e) => {
                                    const calculated = calculateItem(
                                        userInfo,
                                        e.target.value,
                                        values.unitPrice,
                                        values.taxItems
                                    );

                                    setValues({
                                        ...values,
                                        units: e.target.value,
                                        total: calculated.total,
                                        taxItems: calculated.taxItems,
                                    });
                                }}
                                unit={values.uom || ""}
                                size="small"
                                margin="normal"
                                fullWidth
                                error={hasErrors(errors, "units")}
                                helperText={<FormFieldErrors errors={errors} field="units" />}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                label="Jednotka"
                                name="uom"
                                value={values.uom || ""}
                                onChange={handleChange}
                                size="small"
                                margin="normal"
                                fullWidth
                                error={hasErrors(errors, "uom")}
                                helperText={<FormFieldErrors errors={errors} field="uom" />}
                            />
                        </Box>
                        <Box sx={{ flex: 2 }}>
                            <CurrencyField
                                label={
                                    isEmpty(values.uom)
                                        ? "Cena za jednotku"
                                        : `Cena za ${values.uom}`
                                }
                                name="unitPrice"
                                value={values.unitPrice || ""}
                                onChange={(e) => {
                                    const calculated = calculateItem(
                                        userInfo,
                                        values.units,
                                        e.target.value,
                                        values.taxItems
                                    );

                                    setValues({
                                        ...values,
                                        unitPrice: e.target.value,
                                        total: calculated.total,
                                        taxItems: calculated.taxItems,
                                    });
                                }}
                                currencyCode={values.currencyCode}
                                perUnit={values.uom}
                                size="small"
                                margin="normal"
                                fullWidth
                                error={hasErrors(errors, "unitPrice")}
                                helperText={<FormFieldErrors errors={errors} field="unitPrice" />}
                            />
                        </Box>
                    </Stack>

                    <CurrencyField
                        label="Celkem"
                        name="total"
                        value={values.total || ""}
                        onChange={handleChange}
                        currencyCode={values.currencyCode}
                        margin="normal"
                        required
                        fullWidth
                        error={hasErrors(errors, "total")}
                        helperText={<FormFieldErrors errors={errors} field="total" />}
                    />
                </Box>
                {userInfo?.transactionsTaxSupport && (
                    <Box my={2}>
                        <Typography variant="h6" gutterBottom>
                            Daň
                        </Typography>
                        <List>
                            {(values.taxItems || []).map((field, index) => (
                                <TaxField
                                    key={index}
                                    index={index}
                                    disabled={disabled}
                                    errors={errors}
                                    fieldPath={"fields"}
                                    idNumberLabel={idNumberLabel}
                                    options={userInfo.taxRates || []}
                                    value={field}
                                    currencyCode={values.currencyCode}
                                    onChange={(value) => {
                                        setValues({
                                            ...values,
                                            fields: set(values.taxItems, index, value),
                                        });
                                    }}
                                    onDelete={() =>
                                        showDialog(
                                            "Smazat daň",
                                            `Chcete odstranit daň ${field.name || ""}?`,
                                            [
                                                { title: "Storno" },
                                                {
                                                    title: "Smazat",
                                                    variant: "contained",
                                                    primary: true,
                                                    onClick: () =>
                                                        setValues({
                                                            ...values,
                                                            taxItems: values.taxItems.filter(
                                                                (_, i) => i !== index
                                                            ),
                                                        }),
                                                },
                                            ]
                                        )
                                    }
                                />
                            ))}
                        </List>
                        <Button
                            onClick={() => {
                                setValues({
                                    ...values,
                                    taxItems: [...(values.taxItems || []), { base: values.total }],
                                });
                            }}
                            disabled={disabled}
                            startIcon={<AddOutlined />}
                            size="small">
                            Přidat daň
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={hide} disabled={disabled}>
                    Storno
                </Button>
                <Button variant="contained" disabled={disabled} onClick={handleSubmit}>
                    Uložit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const calculateItem = (userInfo, units, unitPrice, taxItems) => {
    units = parseNum(units, userInfo.decimalDelimiter, userInfo.thousandDelimiter);
    unitPrice = parseNum(unitPrice, userInfo.decimalDelimiter, userInfo.thousandDelimiter);

    if (isNaN(unitPrice) || isNaN(units)) {
        return {
            taxItems,
        };
    }

    const total = units * unitPrice;
    const result = {
        units: formatNum(units, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
        unitPrice: formatNum(unitPrice, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
        total: formatNum(total, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
        taxItems: (taxItems || []).map((field) => ({
            ...field,
            base: formatNum(total, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...calculateTaxItem(userInfo, total, field.percentage),
        })),
    };
    return result;
};

export default TransactionItemDialog;

const TaxField = ({
    value,
    onChange,
    options,
    disabled,
    onDelete,
    fieldPath,
    index,
    errors,
    idNumberLabel,
    currencyCode,
}) => {
    const { userInfo } = useUserInfo();
    const taxTypeField = `${fieldPath}.${index}.taxTypeId`;
    const baseField = `${fieldPath}.${index}.base`;
    const taxField = `${fieldPath}.${index}.total`;

    return (
        <ListItem
            dense
            disableGutters
            secondaryAction={
                <IconButton edge="end" disabled={disabled} onClick={() => onDelete?.()}>
                    <DeleteOutlined />
                </IconButton>
            }>
            <Stack direction="row" gap={2} sx={{ flex: 1 }}>
                <Box sx={{ flex: 2 }}>
                    <FormControl
                        fullWidth
                        size="small"
                        margin="normal"
                        error={hasErrors(errors, taxTypeField)}>
                        <InputLabel id={`source-${index}-id`}>Typ daně</InputLabel>
                        <Select
                            labelId={`source-${index}-id`}
                            value={value.taxTypeId || ""}
                            label="Typ daně"
                            onChange={(e) => {
                                const taxType = options?.find(
                                    (option) => option.id === e.target.value
                                );
                                const tax = calculateTaxItem(
                                    userInfo,
                                    value.base,
                                    taxType?.percentage
                                );
                                onChange?.({
                                    ...value,
                                    ...(tax || {}),
                                    taxTypeId: taxType?.id,
                                    taxTypeName: taxType?.name,
                                    percentage: taxType?.percentage,
                                    name: taxType?.name,
                                });
                            }}>
                            {(options || []).map((option) => (
                                <MenuItem value={option.id} key={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {hasErrors(errors, taxTypeField) && (
                            <FormFieldErrors errors={errors} field={taxTypeField} />
                        )}
                    </FormControl>
                </Box>
                <Box sx={{ flex: 2 }}>
                    <CurrencyField
                        label="Základ daně"
                        name="base"
                        value={value.base || ""}
                        onChange={(e) => {
                            const tax = calculateTaxItem(
                                userInfo,
                                e.target.value,
                                value.percentage
                            );
                            onChange?.({ ...value, base: e.target.value, total: tax?.total });
                        }}
                        currencyCode={currencyCode}
                        margin="normal"
                        size="small"
                        fullWidth
                        error={hasErrors(errors, baseField)}
                        helperText={<FormFieldErrors errors={errors} field={baseField} />}
                    />
                </Box>
                <Box sx={{ flex: 2 }}>
                    <CurrencyField
                        label="Daň"
                        name="total"
                        value={value.total || ""}
                        onChange={(e) => onChange?.({ ...value, total: e.target.value })}
                        currencyCode={currencyCode}
                        margin="normal"
                        size="small"
                        fullWidth
                        error={hasErrors(errors, taxField)}
                        helperText={<FormFieldErrors errors={errors} field={taxField} />}
                    />
                </Box>
            </Stack>
        </ListItem>
    );
};
